<script setup>
import { computed } from 'vue'
const sliderValue = defineModel()
const { min, max, step, hint, error, disabled } = defineProps(['min', 'max', 'step', 'hint', 'error', 'disabled'])

const sliderPercent = computed(() => {
  const minVal = parseFloat(min) || 0
  const maxVal = parseFloat(max) || 100
  const val = parseFloat(sliderValue.value) || 0
  return ((val - minVal) / (maxVal - minVal)) * 100
})
</script>

<template>
  <div class="form-field-element">
    <div class="slider-container">
      <div class="slider-input-row">
        <label for="source-value" class="slider-label">Glucose value</label>

        <div class="number-control">
          <div class="number-control-inner">
            <div class="number-input-wrapper">
              <input
                id="source-value"
                placeholder="Glucose value"
                :aria-invalid="error ? 'true' : undefined"
                :aria-describedby="error ? 'source-value-error' : undefined"
                inputmode="decimal"
                type="text"
                class="number-input"
                autofocus
                :disabled="disabled"
                v-model="sliderValue"
              />
            </div>
          </div>
          <p v-if="error" id="source-value-error" class="source-value-error" role="alert">{{ error }}</p>
        </div>
      </div>

      <div class="slider-component-container">
        <div role="slider" class="slider-rail">
          <input
            type="range"
            class="form-range form-control"
            :min="min"
            :max="max"
            :step="step"
            v-model="sliderValue"
            :disabled="disabled"
            :style="{ '--value': sliderPercent + '%' }"
          >
        </div>
        <div class="slider-labels">
          <span class="slider-edge-label">{{ min }}</span>
          <span class="slider-edge-label">{{ max }}</span>
        </div>
      </div>
    </div>
    <div class="field-hint">{{ hint }}</div>
  </div>
</template>

<style>
  .slider-input-row .source-value-error {
    margin: 8px 0 0;
    color: #8B1E1E;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
  }

  .slider-input-row .slider-label {
    text-align: center;
  }
  @media (min-width: 768px) {
    .slider-container .slider-input-row {
      grid-template-columns: minmax(0, 1fr);
    }

    .slider-input-row .slider-label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
      border: 0;
    }

    form.form-container .slider-input-row input.number-input {
      box-sizing: border-box;
      width: 100%;
      height: 88px;
      padding: 16px 24px;
      border: 2px solid #0E8B25;
      border-radius: 16px;
      background-color: #F3FAF4;
      color: #17405D;
      font-size: 40px;
      font-weight: 700;
      line-height: 1.2;
      text-align: center;
      box-shadow: 0 6px 20px rgba(14, 139, 37, 0.12);
    }

    form.form-container .slider-input-row input.number-input::placeholder {
      color: #526779;
      font-size: 24px;
      font-weight: 400;
      opacity: 1;
    }

    form.form-container .slider-input-row input.number-input:focus-visible {
      outline: 3px solid #17405D;
      outline-offset: 3px;
      background-color: #fff;
    }
  }

  input.form-control[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 6px;
    background: linear-gradient(to right, #0E8B25 0%, #0E8B25 var(--value, 0%), #ddd var(--value, 0%), #ddd 100%);
    outline: none;
    margin: 12px 0;
  }

  /* WebKit track fallback */
  input.form-control[type="range"]::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 6px;
    background: transparent;
  }

  /* Thumb - WebKit (Chrome, Edge, Safari) */
  input.form-control[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    /* màu nút */
    border: 2px solid white;
    /* viền để nhìn rõ trên track */
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    margin-top: -5px;
    /* căn giữa theo height */
  }

  /* Firefox */
  input.form-control[type="range"]::-moz-range-track {
    height: 8px;
    border-radius: 6px;
    background: transparent;
  }

  input.form-control[type="range"]::-moz-range-progress {
    background: #ff0000;
    height: 8px;
    border-radius: 6px;
  }

  input.form-control[type="range"]::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ff0000;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }

  /* IE / Edge Legacy */
  input.form-control[type="range"]::-ms-track {
    height: 8px;
    background: transparent;
    border-color: transparent;
    color: transparent;
  }

  input.form-control[type="range"]::-ms-fill-lower {
    background: #ff0000;
    border-radius: 6px;
  }

  input.form-control[type="range"]::-ms-fill-upper {
    background: #ddd;
    border-radius: 6px;
  }

  input.form-control[type="range"]::-ms-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ff0000;
    border: none;
  }

  /* trạng thái disabled */
  input.form-control[type="range"][disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  input.form-control[type="range"][disabled]::-webkit-slider-thumb {
    background: #999;
  }
</style>
